"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useAnalytics } from "@/components/analytics/AnalyticsProvider";
import type { CopilotDirective } from "@/components/copilot/targets";

const MAX_FLASHES = 6;

type SpotlightRect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

// A sidebar group keeps its children out of the page entirely until it is
// opened, so Library and Emails simply do not exist to point at while Talent
// and Company are collapsed. Open the owning group first, then look again.
function openOwningGroup(target: string) {
  const trigger = [
    ...document.querySelectorAll<HTMLElement>("[data-nova-group]"),
  ].find((element) =>
    (element.dataset.novaGroup || "").split(" ").includes(target)
  );
  if (trigger && trigger.getAttribute("aria-expanded") === "false") {
    trigger.click();
    return true;
  }
  return false;
}

function visibleTarget(target: string) {
  const elements = document.querySelectorAll<HTMLElement>(
    `[data-nova-target="${target}"]`
  );
  return [...elements].find((element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.top < window.innerHeight
    );
  });
}

export function NovaSpotlight({
  directive,
  onDismiss,
  onUnavailable,
}: {
  directive: CopilotDirective | null;
  onDismiss: () => void;
  onUnavailable: () => void;
}) {
  const { track } = useAnalytics();
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [flashOn, setFlashOn] = useState(true);
  const shownDirectiveRef = useRef<CopilotDirective | null>(null);
  const clickedDirectiveRef = useRef(false);
  const dismissedDirectiveRef = useRef<CopilotDirective | null>(null);

  const dismissDirective = useCallback(() => {
    if (
      directive &&
      shownDirectiveRef.current === directive &&
      !clickedDirectiveRef.current &&
      dismissedDirectiveRef.current !== directive
    ) {
      dismissedDirectiveRef.current = directive;
      track("nova_directive_dismissed", {
        target: directive.target,
        page: window.location.pathname,
      });
    }
    onDismiss();
  }, [directive, onDismiss, track]);

  useEffect(() => {
    if (!directive) {
      setRect(null);
      return;
    }

    let element = visibleTarget(directive.target);
    if (!element && openOwningGroup(directive.target)) {
      // The group was closed. It is opening now, so try once more on the next
      // frame rather than telling the recruiter it is not here.
      const retry = requestAnimationFrame(() => setRetryToken((n) => n + 1));
      return () => cancelAnimationFrame(retry);
    }
    if (!element) {
      onUnavailable();
      onDismiss();
      return;
    }

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    element.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "center",
      inline: "nearest",
    });
    if (shownDirectiveRef.current !== directive) {
      shownDirectiveRef.current = directive;
      clickedDirectiveRef.current = false;
      dismissedDirectiveRef.current = null;
      track("nova_directive_shown", {
        target: directive.target,
        page: window.location.pathname,
      });
    }

    const measure = () => {
      const next = element.getBoundingClientRect();
      setRect({
        top: next.top,
        left: next.left,
        right: next.right,
        bottom: next.bottom,
      });
    };
    const frame = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    document.addEventListener("scroll", measure, true);
    const clickedTarget = () => {
      if (!clickedDirectiveRef.current) {
        clickedDirectiveRef.current = true;
        track("nova_directive_clicked", {
          target: directive.target,
          page: window.location.pathname,
        });
      }
      onDismiss();
    };
    element.addEventListener("click", clickedTarget, {
      capture: true,
      once: true,
    });
    const dismissOnPointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && element.contains(target)) return;
      if (target instanceof Element && target.closest("[data-nova-callout]")) return;
      dismissDirective();
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissDirective();
    };
    const pointerTimer = window.setTimeout(
      () => document.addEventListener("pointerdown", dismissOnPointer, true),
      250
    );
    document.addEventListener("keydown", dismissOnEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(pointerTimer);
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", measure, true);
      document.removeEventListener("pointerdown", dismissOnPointer, true);
      document.removeEventListener("keydown", dismissOnEscape);
      element.removeEventListener("click", clickedTarget, true);
    };
  }, [directive, dismissDirective, onDismiss, onUnavailable, retryToken, track]);

  useEffect(() => {
    setFlashOn(true);
    if (
      !directive?.blink ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    let toggles = 0;
    const interval = window.setInterval(
      () => {
        toggles += 1;
        setFlashOn((current) => !current);
        if (toggles >= MAX_FLASHES * 2) {
          window.clearInterval(interval);
          setFlashOn(true);
        }
      },
      directive.blink === "urgent" ? 400 : 800
    );
    return () => window.clearInterval(interval);
  }, [directive]);

  if (!directive || !rect) return null;

  const padding = 6;
  const top = Math.max(0, rect.top - padding);
  const left = Math.max(0, rect.left - padding);
  const right = Math.min(window.innerWidth, rect.right + padding);
  const bottom = Math.min(window.innerHeight, rect.bottom + padding);
  const borderColor = directive.tint === "warn" ? "#D97706" : "#266df0";
  const calloutTop =
    bottom + 12 + 88 < window.innerHeight ? bottom + 12 : Math.max(12, top - 100);

  return (
    <div aria-live="polite" className="fixed inset-0 z-[70] pointer-events-none">
      <div className="fixed left-0 right-0 top-0 bg-black/35" style={{ height: top }} />
      <div className="fixed bottom-0 left-0 right-0 bg-black/35" style={{ top: bottom }} />
      <div className="fixed left-0 bg-black/35" style={{ top, bottom: window.innerHeight - bottom, width: left }} />
      <div className="fixed right-0 bg-black/35" style={{ top, bottom: window.innerHeight - bottom, left: right }} />
      <div
        data-nova-callout
        className="fixed rounded-[10px] border-2 shadow-[0_0_0_4px_rgba(255,255,255,0.85)]"
        style={{
          top,
          left,
          width: right - left,
          height: bottom - top,
          borderColor,
          opacity: flashOn ? 1 : 0.35,
        }}
      />
      <div
        data-nova-callout
        // pointer-events-auto: the overlay disables clicks so the recruiter can
        // still use the page underneath, which also disables the button in here.
        className="pointer-events-auto fixed max-w-xs rounded-[10px] bg-white px-3 py-2 text-sm text-foreground shadow-[var(--rx-card-shadow)]"
        style={{
          top: calloutTop,
          left: Math.min(Math.max(12, left), window.innerWidth - 332),
          border: `1px solid ${borderColor}`,
        }}
      >
        <p>{directive.message}</p>
        {directive.cta ? (
          // A real button, not styled text. It looked clickable and did
          // nothing, which is worse than not offering it at all.
          <button
            className="mt-1.5 text-xs font-semibold underline underline-offset-2"
            onClick={() => {
              const element = visibleTarget(directive.target);
              if (element) element.click();
              else dismissDirective();
            }}
            style={{ color: borderColor }}
            type="button"
          >
            {directive.cta}
          </button>
        ) : null}
      </div>
    </div>
  );
}
